/** Shared navigation and contact details for public product pages. */
import { Link } from "react-router-dom";

import { Logo } from "../../components/Logo";
import { FEATURES } from "../features/FeaturePage";
import "./marketing-brand.css";

/** Companion static marketing site (Cloudflare Pages). */
export const MARKETING_ORIGIN = "https://tarii-site.pages.dev";

/** Where a visitor writes to us. */
export const CONTACT_EMAIL = "contact@tarii-time.com";

// ─────────────────────────────────────────────────────────────────────

const NAV_LINK =
  "whitespace-nowrap rounded-[3px] px-2 py-1.5 text-[0.875rem] text-fg-muted hover:text-fg";

/** Shared product navigation: four items on every public page. */
export function MarketingHeader({ context }: { context?: string }) {
  return (
    <header className="flex items-center justify-between gap-3 py-3">
      <div className="flex items-center gap-2 sm:gap-5">
        <Link to="/" aria-label="Back to the Tarii home page" className="shrink-0">
          <Logo className="h-7" />
        </Link>
        <nav aria-label="Tarii" className="flex items-center gap-0.5 sm:gap-2">
          <Link to="/how-it-works" className={NAV_LINK}>
            How it works
          </Link>
          <Link to="/pricing" className={`hidden sm:block ${NAV_LINK}`}>
            Pricing
          </Link>
          <Link to="/demo" className={`hidden sm:block ${NAV_LINK}`}>
            Try the demo
          </Link>
        </nav>
      </div>
      <div className="flex items-center gap-4">
        {context ? (
          <span className="hidden font-mono text-[10px] text-fg-faint uppercase tracking-[0.22em] lg:block">
            {context}
          </span>
        ) : null}
        <Link to="/sign-in" className={NAV_LINK}>
          Sign in
        </Link>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────

const FOOTER_LINK = "text-[0.8125rem] text-fg-subtle hover:text-fg";

/** The flat public footer: one block of links between hairlines. */
export function MarketingFooter() {
  return (
    <footer className="border-border border-t py-10">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:gap-10">
        <Logo className="h-7 w-auto shrink-0 self-start object-contain" />
        <nav aria-label="Tarii pages" className="flex flex-wrap gap-x-6 gap-y-3">
          <Link to="/how-it-works" className={FOOTER_LINK}>
            How it works
          </Link>
          <Link to="/the-problem-with-rostering" className={FOOTER_LINK}>
            The problem with rostering
          </Link>
          {FEATURES.map((f) => (
            <Link key={f.slug} to={`/features/${f.slug}`} className={FOOTER_LINK}>
              {f.name}
            </Link>
          ))}
          <Link to="/pricing" className={FOOTER_LINK}>
            Pricing
          </Link>
          <Link to="/demo" className={FOOTER_LINK}>
            Try the demo
          </Link>
          <Link to="/data-handling" className={FOOTER_LINK}>
            How we handle data
          </Link>
          <Link to="/privacy" className={FOOTER_LINK}>
            Privacy
          </Link>
          <Link to="/terms" className={FOOTER_LINK}>
            Terms
          </Link>
          <a href={`mailto:${CONTACT_EMAIL}`} className={FOOTER_LINK}>
            Talk to us
          </a>
        </nav>
      </div>
    </footer>
  );
}
